import * as React from 'react';
import { useSnackbar } from 'notistack';
import axios from '../../utils/axios';
import { HistoricoGPS } from '../historicoGPS';
import { Num, SeletorStatus, statusTexto } from './index';
import ModalPassos, { PassoModal } from './ModalPassos';
import { Campo, classesEntrada } from './Modal';
import { cn } from '../../lib/utils';

/* ============================================================================
   Cadastro de REGIONAL em modal de cartoes.

   Substitui /dashboard/regionais/new/:codigo. A pagina antiga continua no ar.

   Contrato, o mesmo de antes:
     GET  /regionais/byId/:codigo
     POST /regionais            (criar)
     PUT  /regionais/:codigo    (editar)
     corpo: { nome, descricao, codigo, status, dataCampo }

   ---------------------------------------------------------------------------
   UM CAMPO SO, E O MOTIVO IMPORTA.

   O formulario antigo tem DOIS campos, "Nome" e "Descrição", e faz isto antes de
   enviar (regionais/new/[codigo].tsx):

       if (data.descricao) data.nome = data.descricao;

   Como `descricao` e OBRIGATORIA no schema, ela esta sempre preenchida - logo
   `nome` e SEMPRE sobrescrito por `descricao`. O campo "Nome" nunca teve efeito
   proprio: o que o usuario digitava nele era descartado na linha acima.

   Aqui existe um campo, e ele alimenta as duas colunas - que e o que o sistema
   sempre gravou. Nao e remocao de funcao; e a remocao de um campo que nao fazia
   nada e que fazia parecer que fazia.

   Ainda no antigo: `disabled={oldEntity?.descricao}` no campo de descricao, ou
   seja, uma vez preenchida ela nao podia mais ser alterada pela tela. Isso NAO
   foi reproduzido - nao ha regra de negocio conhecida que impeca renomear uma
   regional, e o backend aceita o PUT. Se a intencao era travar, o lugar e o
   backend, nao um `disabled` que qualquer um contorna pela API.

   ---------------------------------------------------------------------------
   A ABA "GRUPOS" DO FORMULARIO ANTIGO NAO EXISTE.

   Ha um `<Tab label="Grupos" value="2" />` no arquivo antigo, mas NAO existe o
   `<TabPanel value="2">` correspondente - so os de value 1 e 3. Clicar naquela
   aba mostra area vazia. Nao foi reproduzida por isso; para ver os grupos de uma
   regional, a listagem de Grupos tem filtro de regional.
   ============================================================================ */

type Regional = {
  codigo?: number;
  nome?: string;
  descricao?: string;
  status?: number | string;
  visivelTodos?: boolean;
  deletedAt?: string | null;
};

export default function ModalRegional({
  aberto,
  codigo,
  onFechar,
  onSalvo,
}: {
  aberto: boolean;
  /** `undefined` ou '0' = criar. Qualquer outro valor = editar. */
  codigo?: string | number;
  onFechar: () => void;
  onSalvo?: () => void;
}) {
  const { enqueueSnackbar } = useSnackbar();

  const editando = Boolean(codigo && String(codigo) !== '0');

  const [nome, setNome] = React.useState('');
  const [status, setStatus] = React.useState('ATIVO');
  const [original, setOriginal] = React.useState<Regional>({});
  const [salvando, setSalvando] = React.useState(false);
  const [carregando, setCarregando] = React.useState(false);

  const apagada = Boolean(original?.deletedAt);

  React.useEffect(() => {
    if (!aberto) return;

    setNome('');
    setStatus('ATIVO');
    setOriginal({});

    if (!editando) return;

    setCarregando(true);
    axios
      .get<Regional>(`/regionais/byId/${codigo}`)
      .then((r) => {
        const d = r.data || {};
        setOriginal(d);
        // `descricao` e a fonte: e ela que o backend recebe nas duas colunas.
        setNome(d.descricao ?? d.nome ?? '');
        setStatus(statusTexto(d.status));
      })
      .catch((e: any) =>
        enqueueSnackbar(e?.response?.data?.message || 'Não foi possível carregar a regional.', {
          variant: 'error',
        })
      )
      .finally(() => setCarregando(false));
  }, [aberto, codigo, editando, enqueueSnackbar]);

  const nomeOk = nome.trim().length >= 1 && nome.trim().length <= 150;

  async function salvar() {
    if (salvando || !nomeOk) return;
    if (apagada) {
      enqueueSnackbar('Regional foi deletada, não pode alterar mais', { variant: 'warning' });
      return;
    }
    setSalvando(true);
    try {
      const valor = nome.trim();

      const diff: Array<{ campo: string; antigo: any; novo: any }> = [];
      if (editando) {
        if (valor !== original.descricao)
          diff.push({ campo: 'Descrição', antigo: original.descricao, novo: valor });
        if (valor !== original.nome) diff.push({ campo: 'Nome', antigo: original.nome, novo: valor });
        if (status !== String(original.status))
          diff.push({ campo: 'Status', antigo: String(original.status), novo: status });
      }

      await axios[editando ? 'put' : 'post'](`/regionais/${editando ? codigo : ''}`, {
        // As duas colunas com o mesmo valor - e o que o antigo enviava depois do
        // `data.nome = data.descricao`.
        nome: valor,
        descricao: valor,
        codigo: editando ? original.codigo : undefined,
        status,
        dataCampo: editando ? JSON.stringify(diff) : null,
      });

      enqueueSnackbar('Salvo com sucesso', { variant: 'success' });
      onSalvo?.();
      onFechar();
    } catch (err: any) {
      const e = err?.response?.data;
      const msg = e?.errors?.length ? e.errors[0]?.message : e?.message;
      enqueueSnackbar(msg || 'Ops ocorreu um erro no sistema.', {
        variant: 'error',
        style: { whiteSpace: 'pre-line' },
      });
    } finally {
      setSalvando(false);
    }
  }

  const passos: PassoModal[] = [
    {
      id: 'dados',
      titulo: 'Dados',
      grupo: 'Cadastro',
      resumo: nome.trim() || 'sem nome',
      valido: nomeOk && Boolean(status) && !apagada,
      pendencia: !nomeOk ? 'nome' : 'status',
      conteudo: (
        <>
          {apagada && (
            <div className="rounded-lg border border-danger bg-danger-soft p-3 text-[12px] text-danger">
              Esta regional foi <strong>apagada</strong> e não pode mais ser alterada.
            </div>
          )}

          <Campo
            rotulo="Nome"
            obrigatorio
            dica="É gravado como nome e como descrição da regional — o sistema usa o mesmo valor nos dois."
          >
            <input
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              maxLength={150}
              disabled={apagada}
              autoFocus
              className={classesEntrada}
            />
          </Campo>

          <Campo bloco rotulo="Status" obrigatorio>
            <SeletorStatus valor={status} onChange={setStatus} />
          </Campo>

          {editando && (
            <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
              Regional <Num className="text-ink">{original.codigo}</Num>
              {original.visivelTodos ? ' · visível para todos' : ' · restrita'}
            </div>
          )}

          <div className="rounded-lg border border-line bg-surface-2 p-3 text-[12px] text-muted-foreground">
            Quem enxerga uma regional é definido pelos <strong>acessos</strong>, não por este cadastro. Para
            conceder ou aprovar acesso, use Regionais → Acessos.
          </div>
        </>
      ),
    },
  ];

  passos.push({
    id: 'historico',
    titulo: 'Histórico',
    grupo: 'Ferramentas',
    informativo: true,
    conteudo: original.codigo ? (
      <HistoricoGPS tipo={'REGIONAL'} chave={original.codigo} />
    ) : (
      <div className="rounded-lg border border-line bg-surface-2 p-4 text-[12px] text-muted-foreground">
          O histórico começa a ser gravado quando este cadastro for salvo. Cada alteração
          posterior fica registrada aqui com quem fez, quando e o que mudou.
        </div>
    ),
  });

  return (
    <ModalPassos
      aberto={aberto}
      titulo={editando ? 'Editar regional' : 'Nova regional'}
      subtitulo={carregando ? 'Carregando...' : editando ? original.nome || `Regional ${codigo}` : 'Nova regional'}
      passos={passos}
      rotuloFinalizar={editando ? 'Salvar' : 'Criar regional'}
      onFinalizar={salvar}
      onFechar={onFechar}
      salvando={salvando}
    />
  );
}
